// src/routes/index.tsx

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { Spin } from 'antd';
import { useAuth } from '../hooks/useAuth';
import { useAuthContext } from '../contexts/AuthContext';
import RoleBasedProtectedRoute from '../components/ProtectedRoute';
import Staff from '../pages/staff/StaffPage';
import { getDashboardRouteForUser } from '../utils/redirectHelpers';

// Layouts
import AppLayout from '../components/layout/AppLayout';

// Pages
const Dashboard = lazy(() => import('../pages/dashboard/DashboardPage'));
// const DashboardRouter = lazy(() => import('../components/DashboardRouter'));
import DashboardRouter from '../components/DashboardRouter';
const Members = lazy(() => import('../pages/members/Members'));
const Savings = lazy(() => import('../pages/savings/Savings'));
const Credits = lazy(() => import('../pages/credits/Credits'));
const Payments = lazy(() => import('../pages/payments/Payments'));
const Meetings = lazy(() => import('../pages/meetings/Meetings'));
const Reports = lazy(() => import('../pages/reports/Reports'));
const Settings = lazy(() => import('../pages/settings/Settings'));
const Login = lazy(() => import('../pages/auth/Login'));
const ProfileSettingsPage = lazy(() => import('../pages/profile/ProfileSettingsPage'));
const GroupExpenses = lazy(() => import('../pages/expenses/GroupExpenses'));
const OtherIncome = lazy(() => import('../pages/income/OtherIncome'));
const IncomeActivities = lazy(() => import('../pages/income/IncomeActivities'));

// Multi-tenant pages
const Regions = lazy(() => import('../pages/regions/RegionsPage'));
const RegionDetail = lazy(() => import('../pages/regions/RegionDetailPage'));
const Provinces = lazy(() => import('../pages/provinces/ProvincesPage'));
const ProvinceDetail = lazy(() => import('../pages/provinces/ProvinceDetailPage'));
const Villages = lazy(() => import('../pages/villages/VillagesPage'));
const VillageDetail = lazy(() => import('../pages/villages/VillageDetailPage'));
const Communes = lazy(() => import('../pages/communes/CommunesPage'));
const CommuneDetail = lazy(() => import('../pages/communes/CommuneDetailPage'));
const Groups = lazy(() => import('../pages/groups/GroupsPage'));
const GroupDetail = lazy(() => import('../pages/groups/GroupDetailPage'));
const GroupManagement = lazy(() => import('../pages/groups/GroupManagementPage'));

// Contribution pages
const WeeklyContributions = lazy(() => import('../pages/contributions/WeeklyContributions'));
const SolidarityContributions = lazy(() => import('../pages/solidarity/SolidarityContributions'));
const SolidarityRequests = lazy(() => import('../pages/solidarity/SolidarityRequests'));

// Member management pages
const MembershipApprovals = lazy(() => import('../pages/members/MembershipApprovals'));

// Cross-commune reports page (Super Admin only)
const CrossCommuneReports = lazy(() => import('../pages/reports/CrossCommuneReports'));

// Country Reports page (Super Admin only)
const CountryReportsPage = lazy(() => import('../pages/reports/CountryReportsPage'));

// System Analytics page (Super Admin only)
const SystemAnalyticsPage = lazy(() => import('../pages/analytics/SystemAnalyticsPage'));

// Super Admin Dashboard (Super Admin only)
const SuperAdminDashboard = lazy(() => import('../pages/dashboard/SuperAdminDashboard'));

// Staff Permissions Management (Super Admin only)
const StaffPermissionsPage = lazy(() => import('../pages/staff/StaffPermissionsPage'));

// Composant de chargement pour le lazy loading
const Loading = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    background: '#f1f5f9'
  }}>
    <Spin size="large" tip="Chargement..." />
  </div>
);

// Wrapper pour les routes qui nécessitent une authentification
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, isInitialized } = useAuth();

  console.log('ProtectedRoute check:', { isAuthenticated, isLoading, isInitialized });

  // Afficher le spinner de chargement pendant la vérification d'auth ou si pas encore initialisé
  if (isLoading || !isInitialized) {
    console.log('Affichage du spinner de chargement');
    return <Loading />;
  }

  // Rediriger vers login si non authentifié (seulement après que l'initialisation soit complète)
  if (!isAuthenticated) {
    console.log('Non authentifié, redirection vers login');
    return <Navigate to="/login" replace />;
  }

  console.log('Utilisateur authentifié, affichage du contenu protégé');
  return <AppLayout>{children}</AppLayout>;
};

// Wrapper de route publique (pour la page de login)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, isInitialized, user } = useAuth();

  console.log('PublicRoute check:', { isAuthenticated, isLoading, isInitialized });

  // Afficher le spinner de chargement pendant la vérification d'auth ou si pas encore initialisé
  if (isLoading || !isInitialized) {
    console.log('Route publique affichant le chargement');
    return <Loading />;
  }

  // Rediriger vers le tableau de bord si déjà authentifié (seulement après que l'initialisation soit complète)
  if (isAuthenticated) {
    const dashboardRoute = getDashboardRouteForUser(user);
    console.log('Déjà authentifié, redirection vers', dashboardRoute);
    return <Navigate to={dashboardRoute} replace />;
  }

  console.log('Non authentifié, affichage du contenu public');
  return <>{children}</>;
};

// Wrapper de route protégée avec vérification de permissions
const PermissionRoute = ({ 
  children, 
  permission,
  fallbackPath = '/dashboard' 
}: { 
  children: React.ReactNode;
  permission?: string;
  fallbackPath?: string;
}) => {
  const { isAuthenticated, isLoading, isInitialized, hasPermission } = useAuth();

  console.log('PermissionRoute check:', { isAuthenticated, isLoading, isInitialized, permission });

  // Afficher le spinner de chargement pendant la vérification d'auth ou si pas encore initialisé
  if (isLoading || !isInitialized) {
    return <Loading />;
  }

  // Rediriger vers login si non authentifié
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Vérifier la permission si spécifiée
  if (permission && !hasPermission(permission)) {
    console.log(`Permission ${permission} refusée, redirection vers ${fallbackPath}`);
    return <Navigate to={fallbackPath} replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

// Créer la configuration du routeur
const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <PublicRoute>
        <Suspense fallback={<Loading />}>
          <Login />
        </Suspense>
      </PublicRoute>
    ),
  },
  {
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <DashboardRouter />
      </ProtectedRoute>
    ),
  },
  {
    path: '/members/*',
    element: (
      <PermissionRoute permission="members.read">
        <Suspense fallback={<Loading />}>
          <Members />
        </Suspense>
      </PermissionRoute>
    ),
  },
  {
    path: '/members/approvals/*',
    element: (
      <PermissionRoute permission="members.read">
        <Suspense fallback={<Loading />}>
          <MembershipApprovals />
        </Suspense>
      </PermissionRoute>
    ),
  },
  {
    path: '/savings/*',
    element: <Navigate to="/contributions" replace />,
  },
  {
    path: '/credits/*',
    element: (
      <PermissionRoute permission="credits.read">
        <Suspense fallback={<Loading />}>
          <Credits />
        </Suspense>
      </PermissionRoute>
    ),
  },
  {
    path: '/payments/*', // ROUTE SANS PERMISSION (TEMPORAIRE)
    element: (
      <ProtectedRoute>
        <Suspense fallback={<Loading />}>
          <Payments />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/meetings/*',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<Loading />}>
          <Meetings />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/reports/*',
    element: (
      <PermissionRoute permission="reports.read">
        <Suspense fallback={<Loading />}>
          <Reports />
        </Suspense>
      </PermissionRoute>
    ),
  },
  {
    path: '/settings/*',
    element: (
      <PermissionRoute permission="settings.read">
        <Suspense fallback={<Loading />}>
          <Settings />
        </Suspense>
      </PermissionRoute>  
    ),
  },
  {
    path: '/profile',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<Loading />}>
          <ProfileSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: '/staff/permissions',
    element: (
      <RoleBasedProtectedRoute requiredRole="super_admin">
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <StaffPermissionsPage />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/staff/*',
    element: (
      <PermissionRoute permission="staff.read">
        <Suspense fallback={<Loading />}>
          <Staff />
        </Suspense>
      </PermissionRoute>
    ),
  },
  
  // Multi-tenant routes
  {
    path: '/regions',
    element: (
      <RoleBasedProtectedRoute requiredRole="super_admin">
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <Regions />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/regions/:regionId',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <RegionDetail />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/provinces',
    element: (
      <RoleBasedProtectedRoute requiredRole="super_admin">
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <Provinces />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/provinces/:provinceId',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <ProvinceDetail />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/villages',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <Villages />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/villages/:villageId',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <VillageDetail />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/communes',
    element: (
      <RoleBasedProtectedRoute requiredRole="super_admin">
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <Communes />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/communes/:communeId',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <CommuneDetail />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/groups',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <Groups />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/groups/:groupId',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <GroupDetail />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/groups/:groupId/manage',
    element: (
      <RoleBasedProtectedRoute requiredPermissions={['groups.update']}>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <GroupManagement />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  
  // Activities route
  {
    path: '/activities/*',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <IncomeActivities />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  
  // Group Expenses route
  {
    path: '/expenses/*',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <GroupExpenses />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  
  // Weekly Contributions route
  {
    path: '/contributions/*',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <WeeklyContributions />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  
  // Solidarity routes
  {
    path: '/solidarity/contributions/*',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <SolidarityContributions />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  {
    path: '/solidarity/requests/*',
    element: (
      <RoleBasedProtectedRoute>
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <SolidarityRequests />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  // Redirect old solidarity route to contributions for backward compatibility
  {
    path: '/solidarity',
    element: <Navigate to="/solidarity/contributions" replace />,
  },

  // Cross-commune reports (Super Admin only)
  {
    path: '/reports/communes',
    element: (
      <RoleBasedProtectedRoute requiredRole="super_admin">
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <CrossCommuneReports />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },

  // Country-wide reports (Super Admin only)
  {
    path: '/reports/country',
    element: (
      <RoleBasedProtectedRoute requiredRole="super_admin">
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <CountryReportsPage />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },

  // System Analytics (Super Admin only)
  {
    path: '/analytics/system',
    element: (
      <RoleBasedProtectedRoute requiredRole="super_admin">
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <SystemAnalyticsPage />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },

  // Super Admin Dashboard (Super Admin only)
  {
    path: '/dashboard/system',
    element: (
      <RoleBasedProtectedRoute requiredRole="super_admin">
        <AppLayout>
          <Suspense fallback={<Loading />}>
            <SuperAdminDashboard />
          </Suspense>
        </AppLayout>
      </RoleBasedProtectedRoute>
    ),
  },
  
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
]);

export default router;